import type { Account, AccountModule, CreditsData, UserSettings } from '@/api/auth';
import { BonusModule, type DepositBonusSettings } from './bonuses';

/** Offer row from `POST sb/api/player/profile/lostbonuses`. */
export interface LostBonusOffer {
  Id: string;
  Name?: string | null;
  PageTitle?: string | null;
  PageUrl?: string | null;
  /** UsersAPI `Enums.BonusesModules` — Sportsbook / Casino / ExternalSportsbook. */
  ModuleId?: number | null;
}

/** Product chrome for Claim Bonus cards (Figma Sports | Casino). */
export type LostBonusProduct = 'sports' | 'casino';

export function lostBonusProduct(moduleId: number | null | undefined): LostBonusProduct {
  return moduleId === BonusModule.Casino ? 'casino' : 'sports';
}

export interface LostBonusesListRequest {
  MachineGuid: string;
}

export interface LostBonusClaimRequest {
  MachineGuid: string;
  AccountBonusId: string;
}

/** UsersAPI `DepositBonusSettingsMessage` returned on lost-bonus claim. */
export type LostBonusDepositBonusSettings = DepositBonusSettings;

/** Response from `POST sb/api/player/profile/lostbonuscreate`. */
export interface LostBonusClaimResponse {
  Success?: boolean;
  Error?: string | null;
  Result?: string | null;
  Id?: string | null;
  MachineGuid?: string | null;
  Credits?: number;
  CreditsData?: CreditsData | null;
  DepositBonusSettingsMessage?: LostBonusDepositBonusSettings | null;
}

function modulesList(
  account: Account | null | undefined,
  user: UserSettings | null | undefined,
): AccountModule[] {
  const enabled = account?.EnabledModules;
  if (Array.isArray(enabled) && enabled.length > 0) return enabled;
  const userModules = user?.Modules;
  if (Array.isArray(userModules) && userModules.length > 0) return userModules;
  const accountModules = account?.Modules;
  if (Array.isArray(accountModules) && accountModules.length > 0) return accountModules;
  return [];
}

/**
 * Legacy `data-module="lostbonus"` / CheckModules — show Claim Bonus when the
 * brand or player has the `lostbonus` module enabled.
 */
export function isLostBonusEnabled(
  account: Account | null | undefined,
  user?: UserSettings | null,
): boolean {
  return modulesList(account, user).some(
    (mod) => typeof mod?.Name === 'string' && mod.Name.toLowerCase() === 'lostbonus',
  );
}

export function lostBonusOfferTitle(offer: LostBonusOffer): string {
  return offer.Name?.trim() || '';
}

export function lostBonusOfferDescription(
  offer: LostBonusOffer,
  fallback: string,
): string {
  const title = offer.PageTitle?.trim();
  return title || fallback;
}

export function lostBonusProductLabel(
  product: LostBonusProduct,
  label: (key: string, fallback: string) => string,
): string {
  return product === 'casino'
    ? label('Casino', 'Casino')
    : label('Sports', 'Sports');
}
